import React, {
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
  useEffect,
} from 'react';
import type { HistoryItem as CrashHistoryItem } from '@platform/contracts';
import { resolveGameStoreHook } from '@core/gameengine/state/activeGameStore';
import { useChat, getUnreadChatCount } from '@core/gameengine/state/chat';
import {
  formatUnreadChatCount,
  getChatUnreadCountBadgeStyle,
  getChatUnreadDotStyle,
} from '@core/lobby/chatUnreadIndicator';
import { getGameFromUrl } from '@core/utils/urlParser';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';
import type { HistoryItem as AviatorHistoryItem } from '@platform/contracts';
import { historyStyles } from '../styles/History.styles';
import { useTheme } from '@core/UI/context/ThemeContext';
import '../styles/HistoryAnimations.css';
import { getImageAsset } from '../utils/getAssetHelper';
import { ProvablyFairService } from '@Web/services/provablyfairservice';
import { GlobalBus } from '@core/Bus/EventBus';
import { formatDecimal, formatMultiplier, useT } from '@core/i18n';

const FULL_HD_WIDTH = 1920;

type HistoryItem = CrashHistoryItem | AviatorHistoryItem | any;

interface HistoryViewProps {
  controller?: any; // Optional controller to determine which game state to use
}

export const HistoryView: React.FC<HistoryViewProps> = ({ controller }) => {
  const gameSlug = controller?.getGameSlug?.() || 'crash';
  const urlSlug = getGameFromUrl() || '';
  const isSkyxNew = isSkyxNewRoute(urlSlug || gameSlug);
  const t = useT(isSkyxNew ? 'skyx_new' : undefined);
  const hasUnreadChat = useChat(state => state.hasUnread);
  const unreadChatCount = useChat(getUnreadChatCount);
  const { theme } = useTheme();
  const useGameStore = resolveGameStoreHook(controller);
  const platformUIConfig = controller?.getPlatformUIConfig?.() || {};
  const uiProfile = platformUIConfig.profile;
  const historyChipColors = platformUIConfig.historyChipColors;
  const isSkySniffer =
    gameSlug === 'skysniffer' ||
    gameSlug === 'sky-sniffer' ||
    urlSlug === 'sky-sniffer' ||
    urlSlug === 'skysniffer';
  const usesCompactPurpleProfile = uiProfile === 'compactPurple';
  const showSkyxHistoryToggleFade =
    usesCompactPurpleProfile && isSkyxNewRoute(urlSlug || gameSlug);
  const usesTextHistoryChips =
    Boolean(historyChipColors) || usesCompactPurpleProfile;
  const _GAME_CODE = useGameStore((state: any) => state.gameId);
  const TABLE_NO = useGameStore((state: any) => state.tableId);

  // -------------------------------------------------------------------------
  // 1. UNCONDITIONAL HOOK CALLS
  // We must call both sets of hooks every time to satisfy React's rules.
  // -------------------------------------------------------------------------

  const deviceType = useGameStore((s: any) => s.deviceType);
  const isLandscape = useGameStore((s: any) => s.isLandscape);
  const isIpad = useGameStore((s: any) => s.isIpad);
  const history = useGameStore((s: any) => s.history || []);
  const balance = useGameStore((s: any) => s.balance);
  const currency = useGameStore((s: any) => s.currency);
  const openModal = useGameStore((s: any) => s.openProvablyFairModal);

  // -------------------------------------------------------------------------
  // 3. ASSETS
  // -------------------------------------------------------------------------
  const closeIconUrl = controller
    ? controller.getAssetUrl(
        getImageAsset(gameSlug, 'crossIcon', controller.getAssetManifest?.())
      )
    : '';

  const showmoreIconUrl = controller
    ? controller.getAssetUrl(
        getImageAsset(gameSlug, 'showmoreIcon', controller.getAssetManifest?.())
      )
    : '';

  const chatIconUrl = controller
    ? controller.getAssetUrl(
        getImageAsset(gameSlug, 'chatIcon', controller.getAssetManifest?.())
      )
    : '';

  const handleChatToggle = () => GlobalBus.emit('chat:toggle', {});

  // -------------------------------------------------------------------------
  // 4. STYLES & STATE
  // -------------------------------------------------------------------------
  const styles = useMemo(() => {
    const handheldLayout =
      platformUIConfig.resolveHistoryHandheldLayout?.(
        deviceType,
        isLandscape
      ) ?? null;

    try {
      return historyStyles(
        theme,
        deviceType,
        isLandscape,
        isIpad,
        isSkySniffer || usesCompactPurpleProfile,
        usesCompactPurpleProfile,
        handheldLayout
      );
    } catch (error) {
      console.error('[HistoryView] Error generating styles:', error);
      // Return minimal styles as fallback
      return {
        historyWrap: {
          display: 'flex',
          alignItems: 'center',
          position: 'relative' as const,
          height: '32px',
          width: '100%',
        },
        payoutsWrapper: {
          display: 'flex',
          alignItems: 'center',
          position: 'relative' as const,
          height: '100%',
          width: '100%',
          overflow: 'hidden',
        },
        historyChip: {
          padding: '2px 5.5px',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '500',
          cursor: 'pointer',
        },
        toggleButtonContainer: {
          position: 'relative' as const,
          display: 'flex',
          alignItems: 'center',
        },
        historyToggleButton: {
          position: 'relative' as const,
          height: '16px',
          padding: '0 4px',
          borderRadius: '32px',
          background: '#2c2d30',
          cursor: 'pointer',
          border: 'none',
          color: '#a0a3af',
        },
        ellipsis: { fontSize: '13px', fontWeight: 700, lineHeight: 1 },
        historyExpandedOverlay: {
          position: 'relative' as const,
          flexDirection: 'column',
          padding: '8px',
          background: '#1b1c1d',
          minHeight: '80px',
          borderRadius: '16px',
        },
        historyHeader: {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '4px',
          width: '100%',
        },
        historyTitle: { fontSize: '12px', fontWeight: '500', color: '#a3a3a3' },
        historyToggleCloseButton: {
          background: 'rgba(50, 50, 50, 0.8)',
          borderRadius: '45%',
          border: 'none',
          color: '#a0a3af',
          width: '24px',
          height: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          cursor: 'pointer',
          padding: 0,
        },
        historyContent: {
          display: 'flex',
          flexWrap: 'wrap',
          rowGap: '4px',
          columnGap: '2px',
          overflowY: 'auto',
        },
      } as ReturnType<typeof historyStyles>;
    }
  }, [
    theme,
    deviceType,
    isLandscape,
    isIpad,
    isSkySniffer,
    usesCompactPurpleProfile,
    platformUIConfig,
  ]);

  const [isExpanded, setExpanded] = useState(false);
  const [animatingItems, setAnimatingItems] = useState<Set<string | number>>(
    new Set()
  );
  const prevHistoryLength = useRef(history.length);

  const historyWrapRef = useRef<HTMLDivElement>(null);
  // Ref to detect clicks outside the expanded overlay
  const overlayRef = useRef<HTMLDivElement>(null);

  const [visibleItemCount, setVisibleItemCount] = useState(25);
  const handleHistoryClick = async (item: HistoryItem) => {
    const roundNo = Number(item.roundId);

    if (isSkyxNew && Number.isFinite(roundNo)) {
      openModal({
        ...item,
        roundId: roundNo,
        serverSeed: item.serverSeed ?? '',
        serverSeedHash: item.serverSeedHash ?? '',
        timeStamp: item.timeStamp ?? 0,
      });
      return;
    }

    try {
      const response = await ProvablyFairService.getInstance().getRoundHistory(
        TABLE_NO,
        roundNo
      );

      console.log('Provably Fair Data:', response);
      openModal({
        ...item,
        serverSeed: response?.serverSeed ?? 'N/A',
        timeStamp: response?.endedAt ?? 'N/A',
        client_seed: response?.clientSeed ?? 'N/A',
        server_seed_hash: response?.serverSeedHash ?? 'N/A',
      });
    } catch (error) {
      console.error('Error fetching server seed:', error);
      openModal({
        ...item,
        server_seed: 'Error',
      });
    }
  };

  // -------------------------------------------------------------------------
  // 5. EFFECTS
  // -------------------------------------------------------------------------

  // Calculate how many items fit in the collapsed view
  useLayoutEffect(() => {
    const isFullHDDesktop = () => {
      return (
        deviceType === 'desktop' &&
        // Using a generous range to account for browser chrome/scaling
        window.innerWidth >= FULL_HD_WIDTH - 100 &&
        window.innerWidth <= FULL_HD_WIDTH + 100
      );
    };

    // 1. Full HD Desktop specific rule
    if (isFullHDDesktop()) {
      setVisibleItemCount(33);
      return;
    }

    // 2. Standard Fixed Device rule
    if (
      deviceType === 'desktop' ||
      deviceType === 'mobile' ||
      deviceType === 'tablet'
    ) {
      setVisibleItemCount(25);
      return;
    }

    // 3. Dynamic calculation
    const calculateItems = () => {
      if (historyWrapRef.current) {
        const containerWidth = historyWrapRef.current.offsetWidth;
        // Reserve space for the button and padding
        const availableWidth = containerWidth - 60;
        const averageItemWidth = 45;
        const count = Math.floor(availableWidth / averageItemWidth);
        setVisibleItemCount(Math.max(1, count));
      }
    };

    calculateItems();
    window.addEventListener('resize', calculateItems);
    return () => window.removeEventListener('resize', calculateItems);
  }, [deviceType]);

  // Trigger animation when new history is added
  useEffect(() => {
    if (history.length > prevHistoryLength.current && history.length > 0) {
      const allItemIds = new Set(
        history.map((item: HistoryItem) => item.roundId)
      );
      setAnimatingItems(allItemIds as Set<string | number>);

      const timer = setTimeout(() => {
        setAnimatingItems(new Set());
      }, 850);

      return () => clearTimeout(timer);
    }
    prevHistoryLength.current = history.length;
  }, [history]);

  const isSkyxNewHandheld =
    showSkyxHistoryToggleFade && deviceType !== 'desktop';

  // skyx_new mobile/tablet: let expanded panel paint outside the fixed #history slot.
  useEffect(() => {
    if (!isSkyxNewHandheld) {
      return;
    }

    const historyHost = document.getElementById('history');

    if (isExpanded) {
      document.body.classList.add('skyx-history-expanded');
      historyHost?.classList.add('skyx-history-expanded');
    } else {
      document.body.classList.remove('skyx-history-expanded');
      historyHost?.classList.remove('skyx-history-expanded');
    }

    return () => {
      document.body.classList.remove('skyx-history-expanded');
      historyHost?.classList.remove('skyx-history-expanded');
    };
  }, [isExpanded, isSkyxNewHandheld]);

  // skyx_new handheld: close expanded overlay on orientation change so layout re-applies cleanly
  useEffect(() => {
    if (!usesCompactPurpleProfile || !isSkyxNewRoute(urlSlug || gameSlug)) {
      return;
    }

    const resetHistoryLayout = () => {
      setExpanded(false);
      document.body.classList.remove('skyx-history-expanded');
      const historyHost = document.getElementById('history');
      historyHost?.classList.remove('skyx-history-expanded');
      for (const id of ['history', 'history-component-root']) {
        const el = document.getElementById(id);
        if (!el) continue;
        el.style.transform = '';
        el.style.transition = '';
        el.style.willChange = '';
      }
    };

    const handleOrientationChange = () => {
      resetHistoryLayout();
      window.setTimeout(resetHistoryLayout, 100);
      window.setTimeout(resetHistoryLayout, 300);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.visualViewport?.addEventListener('resize', handleOrientationChange);
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.visualViewport?.removeEventListener(
        'resize',
        handleOrientationChange
      );
    };
  }, [gameSlug, urlSlug, usesCompactPurpleProfile]);

  // Handle Clicking Outside to Close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isExpanded &&
        overlayRef.current &&
        !overlayRef.current.contains(event.target as Node)
      ) {
        setExpanded(false);
      }
    }

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  // -------------------------------------------------------------------------
  // 6. RENDER HELPERS
  // -------------------------------------------------------------------------

  const getColor = (x: number) => {
    if (historyChipColors) {
      if (x >= 10) {
        return { color: historyChipColors.high };
      }
      if (x >= 2) {
        return { color: historyChipColors.medium };
      }
      return { color: historyChipColors.low };
    }

    if (
      gameSlug === 'skysniffer' ||
      gameSlug === 'sky-sniffer' ||
      usesCompactPurpleProfile
    ) {
      if (usesCompactPurpleProfile) {
        if (x >= 10) {
          return { color: '#ea3ed1' };
        }
        if (x >= 2) {
          return { color: '#b370c5' };
        }
        return { color: '#ffffff' };
      }
      if (x >= 10) {
        return {
          background: '#6b3fa0',
          border: '1px solid #9c6bd6',
        };
      }
      if (x >= 2) {
        return {
          background: '#2c6e8f',
          border: '1px solid #4fa6c9',
        };
      }
      return {
        background: '#b33a3a',
        border: '1px solid #f58484',
      };
    }
    if (x >= 10)
      return {
        background:
          'linear-gradient(135deg, rgba(192, 23, 180, 0.3) 0%, rgba(160, 20, 140, 0.4) 100%)',
        border: '1px solid rgb(220, 50, 200)',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15)',
      };
    if (x >= 2)
      return {
        background:
          'linear-gradient(135deg, rgba(145, 62, 248, 0.3) 0%, rgba(120, 40, 230, 0.4) 100%)',
        border: '1px solid rgb(170, 90, 255)',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15)',
      };
    return {
      background:
        'linear-gradient(135deg, rgba(52, 180, 255, 0.3) 0%, rgba(30, 160, 240, 0.4) 100%)',
      border: '1px solid rgb(80, 200, 255)',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.15)',
    };
  };

  const visibleHistory = history.slice(0, visibleItemCount);
  const showExpandButton = history.length >= 25;
  const expandedHistory = history.slice(0, 60);

  const skyxToggleButtonStyle: React.CSSProperties = {
    ...styles.historyToggleButton,
    ...(usesCompactPurpleProfile
      ? {
          background: '#0e0430',
          border: '1px solid #a13ef8',
          color: '#ffffff',
          marginRight: 0,
          marginTop: 0,
        }
      : {}),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const renderSkyxHistoryToggleButton = (expanded: boolean) => (
    <button
      style={skyxToggleButtonStyle}
      onClick={() => setExpanded(expanded ? false : true)}
      aria-label={expanded ? t('ui.common.close') : t('ui.common.show_more')}
    >
      <img
        src={expanded ? closeIconUrl : showmoreIconUrl}
        alt=""
        style={{
          width: '16px',
          height: '16px',
          display: 'block',
        }}
      />
    </button>
  );

  // -------------------------------------------------------------------------
  // 7. MAIN RENDER
  // -------------------------------------------------------------------------

  if (isExpanded) {
    const expandedPanelStyle: React.CSSProperties = usesCompactPurpleProfile
      ? {
          ...styles.historyWrap,
          ...styles.historyExpandedOverlay,
          ...(platformUIConfig.historyWrapStyle || {}),
        }
      : styles.historyExpandedOverlay;

    return (
      <>
        <div
          ref={overlayRef}
          id={usesCompactPurpleProfile ? 'history-component-root' : undefined}
          style={expandedPanelStyle}
        >
          <div style={styles.historyHeader}>
            <span style={styles.historyTitle}>
              {t('ui.game_info.round_history')}
            </span>
            {!usesCompactPurpleProfile && (
              <button
                style={styles.historyToggleCloseButton}
                onClick={() => setExpanded(false)}
              >
                <img
                  src={closeIconUrl}
                  alt={t('ui.common.close')}
                  style={{
                    width: '16px',
                    height: '16px',
                    display: 'block',
                  }}
                />
              </button>
            )}
          </div>
          {usesCompactPurpleProfile && (
            <div style={styles.historyToggleFixedSlot}>
              {renderSkyxHistoryToggleButton(true)}
            </div>
          )}
          <div style={styles.historyContent}>
            {expandedHistory.map((item: HistoryItem) => {
              return (
                <div
                  key={item.roundId}
                  role="button"
                  tabIndex={0}
                  style={{
                    ...styles.historyChip,
                    ...getColor(item.bust),
                    color: usesTextHistoryChips
                      ? (getColor(item.bust) as any).color
                      : '#fff',
                    ...(usesTextHistoryChips
                      ? {
                          fontFamily:
                            "'Roboto Condensed', 'Roboto', sans-serif",
                          fontWeight: 500,
                          background: 'transparent',
                          border: 'none',
                          padding: '0 6px',
                          marginRight: '10px',
                          borderRadius: '0',
                          fontSize: '12px',
                        }
                      : {}),
                  }}
                  onClick={() => handleHistoryClick(item)}
                >
                  {formatMultiplier(item.bust)}
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  const chipsContent = (
    <div
      style={{
        ...styles.historyWrap,
        ...(platformUIConfig.historyWrapStyle || {}),
        ...(isSkySniffer ? { height: '42px', flex: 1, minWidth: 0 } : {}),
      }}
      ref={historyWrapRef}
      id="history-component-root"
    >
      <div style={styles.payoutsWrapper}>
        {visibleHistory.map((item: HistoryItem, index: number) => {
          const isAnimating = animatingItems.has(item.roundId);
          const isNewItem = index === 0 && isAnimating;

          return (
            <div
              key={item.roundId}
              role="button"
              tabIndex={0}
              className={
                isNewItem
                  ? 'history-chip-new'
                  : isAnimating
                    ? 'history-chip-slide'
                    : ''
              }
              style={{
                ...styles.historyChip,
                ...getColor(item.bust),
                color: usesTextHistoryChips
                  ? (getColor(item.bust) as any).color
                  : '#fff',
                ...(usesTextHistoryChips
                  ? {
                      fontFamily: "'Roboto Condensed', 'Roboto', sans-serif",
                      fontWeight: 500,
                      background: 'transparent',
                      border: 'none',
                      minWidth: 'auto',
                      height: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '0',
                      padding: '0 6px',
                      marginRight: '10px',
                      fontSize: '12px',
                      lineHeight: 1,
                      boxSizing: 'border-box' as const,
                    }
                  : {}),
                ...(isSkySniffer
                  ? {
                      width: '48px',
                      height: '26px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box' as const,
                    }
                  : {}),
              }}
              onClick={() => handleHistoryClick(item)}
            >
              {formatMultiplier(item.bust)}
            </div>
          );
        })}
        {showSkyxHistoryToggleFade && showExpandButton && (
          <div style={styles.fadeOverlay} aria-hidden="true" />
        )}
      </div>
      <div
        style={{
          ...styles.toggleButtonContainer,
          ...(usesCompactPurpleProfile ? styles.historyToggleFixedSlot : {}),
        }}
      >
        {showExpandButton &&
          (usesCompactPurpleProfile ? (
            renderSkyxHistoryToggleButton(false)
          ) : (
            <button
              style={{
                ...styles.historyToggleButton,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => setExpanded(true)}
            >
              <img
                src={showmoreIconUrl}
                alt={t('ui.common.show_more')}
                style={{
                  width: '16px',
                  height: '16px',
                  display: 'block',
                }}
              />
            </button>
          ))}
      </div>
    </div>
  );

  if (isSkySniffer) {
    return (
      <>
        <div
          className="ss-history-chips-container"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '1424px',
            height: '46px',
          }}
        >
          {chipsContent}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              marginLeft: '8px',
              gap: '4px',
              whiteSpace: 'nowrap' as const,
              userSelect: 'none' as const,
            }}
          >
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                color: '#000000',
                lineHeight: '16px',
              }}
            >
              {formatDecimal(balance, {
                currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </span>
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '16px',
                color: '#000000',
                lineHeight: '16px',
              }}
            >
              {currency || '₹'}
            </span>
            {chatIconUrl ? (
              <button
                type="button"
                data-chat-toggle
                onClick={handleChatToggle}
                style={{
                  position: 'relative',
                  width: '28px',
                  height: '28px',
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
                aria-label={t('ui.chat.open')}
              >
                <img
                  src={chatIconUrl}
                  alt=""
                  style={{
                    width: '22px',
                    height: '22px',
                    objectFit: 'contain',
                  }}
                />
                {hasUnreadChat ? (
                  isSkyxNew ? (
                    <span
                      style={getChatUnreadCountBadgeStyle(
                        '#ffffff',
                        unreadChatCount
                      )}
                      aria-label={`${formatUnreadChatCount(unreadChatCount)} unread messages`}
                    >
                      {formatUnreadChatCount(unreadChatCount)}
                    </span>
                  ) : (
                    <span
                      aria-hidden="true"
                      style={getChatUnreadDotStyle('#ffffff')}
                    />
                  )
                ) : null}
              </button>
            ) : null}
            <div
              style={{
                width: '28px',
                height: '28px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  width: '16px',
                  height: '2px',
                  backgroundColor: '#000000',
                  borderRadius: '2px',
                  display: 'block',
                }}
              />
              <span
                style={{
                  width: '16px',
                  height: '2px',
                  backgroundColor: '#000000',
                  borderRadius: '2px',
                  display: 'block',
                }}
              />
              <span
                style={{
                  width: '16px',
                  height: '2px',
                  backgroundColor: '#000000',
                  borderRadius: '2px',
                  display: 'block',
                }}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  return <>{chipsContent}</>;
};
